import { useHistory } from 'react-router';
import { useLeasingUrlParams } from '../../hooks';

import { LeaseArtifactType } from '@zg-rentals/ts-leasing-client';

import {
  getCosmosJourneyToken,
  useLeaseExtensionQuery,
  useLeaseQuery,
  useLeaseSummaryQuery,
  usePropertyInfoQuery,
  useRenterUser,
} from '../../utils/queries';
import LeaseExtension from '../../models/LeaseExtension';
import { ReviewRenderer } from '@zillow/cosmos';
import { useEffect, useState } from 'react';
import { Alert, Box, Card, TextButton } from '@zillow/constellation-10';
import { LoadingButton, LoadingHeading, LoadingText } from '@zg-rentals/loading-components';
import LeaseActionButtonGroup from '../../components/LeaseActions/LeaseActionButtonGroup';
import type { LeaseAction } from '../../models';
import { Lease } from '../../models';
import { useIsFetching } from '@zg-rentals/react-query5';
import SummaryTitle from '../../components/LeaseSummary/SummaryTitle';

export function TenantSummaryView() {
  const history = useHistory();
  const { leaseId, renewalId } = useLeasingUrlParams();
  const queryParameters = new URLSearchParams(window.location.search);

  const [isReviewRendererLoading, setIsReviewRendererLoading] = useState(false);
  const [hasRenderedReviewRenderer, setHasRenderedReviewRenderer] = useState(false);

  const isFetching = useIsFetching();

  const leaseQuery = useLeaseQuery(leaseId);
  const leaseExtensionQuery = useLeaseExtensionQuery(renewalId);
  const propertyInfoQuery = usePropertyInfoQuery(
    leaseExtensionQuery?.data?.listingAlias,
    leaseExtensionQuery.isFetched,
  );
  const leaseSummaryQuery = useLeaseSummaryQuery(renewalId, LeaseArtifactType.LEASE_EXTENSION_RENEWAL);
  const renterSignedInUserQuery = useRenterUser();
  const tenant = leaseExtensionQuery?.data?.tenants?.find(
    (tenant) => tenant?.email === renterSignedInUserQuery?.data?.email,
  );

  const leaseExtension = new LeaseExtension(leaseExtensionQuery?.data);

  async function generateJourneyToken() {
    return getCosmosJourneyToken(LeaseArtifactType.LEASE_EXTENSION_RENEWAL, renewalId);
  }

  useEffect(() => {
    if (leaseExtensionQuery.isError) {
      //history.push(LeaseExtension.getDashboardPath());
    }
  }, [leaseExtensionQuery.isError]);

  useEffect(() => {
    if (leaseExtension?.journeyId) {
      setHasRenderedReviewRenderer(true);
      setIsReviewRendererLoading(true);
    }
  }, [leaseExtension?.journeyId]);

  function onViewOriginalLease() {
    history.push(Lease.getSummaryPath(leaseId));
  }

  const showSkeleton =
    !leaseExtensionQuery.isFetched || !hasRenderedReviewRenderer || isReviewRendererLoading || isFetching > 0;

  const skeleton = (
    <>
      <Card tone="neutral" elevated={false} css={{ marginBottom: 'tight' }}>
        <LoadingText isLoading loadingWidth="90%" css={{ marginBottom: 'tighter' }}>
          Loading
        </LoadingText>
        <LoadingButton isLoading>Action</LoadingButton>
      </Card>
      <LoadingHeading isLoading level={1} loadingWidth="60%" css={{ marginBottom: 'tight' }}>
        Loading
      </LoadingHeading>
      <LoadingText isLoading loadingWidth="40%" css={{ marginBottom: 'tighter' }}>
        Loading
      </LoadingText>
      <LoadingText isLoading loadingWidth="50%" css={{ marginBottom: 'tight' }}>
        Loading
      </LoadingText>
      <Box css={{ display: 'flex', gap: 'tighter', marginBottom: 'tight' }}>
        <LoadingButton isLoading>Action</LoadingButton>
        <LoadingButton isLoading>Action</LoadingButton>
      </Box>
      {[1, 2, 3].map((index) => (
        <Card key={index} tone="neutral" elevated={false} css={{ marginBottom: 'tighter' }}>
          <LoadingHeading isLoading level={2} loadingWidth="40%" css={{ marginBottom: 'tight' }}>
            Loading
          </LoadingHeading>
          <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 'tight' }}>
            <Box css={{ gridColumnEnd: { base: 'span 12', md: 'span 4' } }}>
              <LoadingText isLoading loadingWidth="80%" css={{ marginBottom: 'tighter' }}>
                Loading
              </LoadingText>
            </Box>
            <Box css={{ gridColumnEnd: { base: 'span 12', md: 'span 8' } }}>
              <LoadingText isLoading loadingWidth="90%" css={{ marginBottom: 'tighter' }}>
                Loading
              </LoadingText>
            </Box>
            <Box css={{ gridColumnEnd: { base: 'span 12', md: 'span 4' } }}>
              <LoadingText isLoading loadingWidth="80%" css={{ marginBottom: 'tighter' }}>
                Loading
              </LoadingText>
            </Box>
            <Box css={{ gridColumnEnd: { base: 'span 12', md: 'span 8' } }}>
              <LoadingText isLoading loadingWidth="90%" css={{ marginBottom: 'tighter' }}>
                Loading
              </LoadingText>
            </Box>
          </Box>
        </Card>
      ))}
      <Box css={{ display: 'flex', gap: 'tighter', marginBottom: 'tight' }}>
        <LoadingButton isLoading>Action</LoadingButton>
        <LoadingButton isLoading>Action</LoadingButton>
      </Box>
    </>
  );

  if (leaseExtensionQuery.isError) {
    return (
      <Alert
        tone="warning"
        actionButton={
          <TextButton asChild>
            <a
              href={`/user/acct/login/?url=%2Frenter-hub%2Fleasing%2Flease%2F${leaseId}%2Frenewal%2F${renewalId}${encodeURIComponent(
                `?${queryParameters.toString()}`,
              )}`}
            >
              Switch accounts
            </a>
          </TextButton>
        }
      >
        You’re signed in as {renterSignedInUserQuery?.data?.email}. This email isn’t connected to this lease. Sign in
        with the email address linked to your lease.
      </Alert>
    );
  }

  return (
    <>
      {showSkeleton && skeleton}
      <Box css={{ visibility: showSkeleton ? 'hidden' : 'visible', height: showSkeleton ? '0' : 'auto' }}>
        {leaseExtensionQuery.isFetched && (
          <>
            <Alert
              tone="info"
              actionButton={<TextButton onClick={onViewOriginalLease}>View original lease</TextButton>}
              css={{ marginBottom: 'tight' }}
            >
              This lease renewal agreement extends the terms of another lease
            </Alert>
            <SummaryTitle
              {...{
                summaryText: leaseSummaryQuery?.data?.summaryText,
                propertyAddress: propertyInfoQuery?.data?.streetAddress,
                personFirstName: tenant?.firstName,
                leaseStartDate: leaseExtension.getFriendlyStartDate(),
                hasColandlord: leaseExtensionQuery?.data?.landlords?.length > 1,
              }}
            />
            <LeaseActionButtonGroup
              leaseActions={leaseSummaryQuery?.data?.summaryActions as Array<LeaseAction>}
              lease={leaseQuery?.data}
              isTop={true}
              fluid
            />
            <Box css={{ marginBottom: 'tight' }} />
            {leaseExtension && leaseExtension?.journeyId && (
              <ReviewRenderer
                journeyId={leaseExtension.journeyId}
                journeyVersionId={leaseExtension.journeyVersionId}
                generateJourneyToken={generateJourneyToken}
                isReadOnly
                setIsReviewRendererLoading={setIsReviewRendererLoading}
              />
            )}
            <Box css={{ marginBottom: 'tight' }} />
            <LeaseActionButtonGroup
              leaseActions={leaseSummaryQuery?.data?.summaryActions as Array<LeaseAction>}
              lease={leaseQuery?.data}
              isTop={false}
              fluid
            />
          </>
        )}
      </Box>
    </>
  );
}
